import React, {
  useCallback, useContext, useMemo, useRef, useState,
} from 'react';
import { BaseMessage } from '@bingads-webui/ads-copilot-ui';
import { dispatchStructuredLogUserEngagementEvent } from '@bingads-webui-react/copilot-logger';
import {
  Button, Label, Radio, RadioGroup, Textarea, makeStyles, mergeClasses, shorthands,
} from '@fluentui/react-components';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { ILogger } from '@bingads-webui-react/msa-copilot-common';
import { CheckmarkCircle20Filled, Star16Filled, Star16Regular } from '@fluentui/react-icons';
import { cvSurveyConfig } from '../helpers';

interface SurveyResponsesProps {
  conversationId: string;
  firstName: string;
  lastName: string;
  localeId: string;
  localeName: string;
  isSatisfied: string;
  rating: number;
  additionalInfo: string;
  startDate: string;
  submitDate: string;
}

const sendSurveyResponse = (logger: ILogger, surveyResponses: SurveyResponsesProps) => {
  try {
    const myHeaders = new Headers();
    myHeaders.append('Accept', 'application/json');
    myHeaders.append('Content-Type', 'application/x-www-form-urlencoded');
    myHeaders.append('Contextparams', JSON.stringify({
      ConversationId: surveyResponses.conversationId,
      'First Name': encodeURIComponent(surveyResponses.firstName),
      'Last Name': encodeURIComponent(surveyResponses.lastName),
    }));

    const urlencoded = new URLSearchParams();
    const surveyAnswers = [
      { questionId: cvSurveyConfig.choiceQuestionId, answer1: surveyResponses.isSatisfied },
      { questionId: cvSurveyConfig.ratingQuestionId, answer1: surveyResponses.rating },
      { questionId: cvSurveyConfig.textQuestionId, answer1: surveyResponses.additionalInfo },
    ];
    const language = { localeId: surveyResponses.localeId, localeName: surveyResponses.localeName };
    urlencoded.append('answers', JSON.stringify(surveyAnswers));
    urlencoded.append('submitLanguage', JSON.stringify(language));
    urlencoded.append('startDate', surveyResponses.startDate);
    urlencoded.append('submitDate', surveyResponses.submitDate);

    fetch(
      cvSurveyConfig.feedbackUrl,
      {
        method: 'POST',
        headers: myHeaders,
        body: urlencoded,
      },
    )
      .then(() => logger?.trace('Copilot - Live Agent: Survey response sent successfully'))
      .catch((error) => logger?.error(`Copilot - Live Agent: Network request failed - ${error}`));
  } catch (error) {
    logger?.error(`Copilot - Live Agent: Error sending survey response - ${error}`);
  }
};

const useRatingStyles = makeStyles({
  button: {
    ...shorthands.padding('0'),
    minWidth: 'auto',
    width: '16px',
  },
  starFilled: {
    color: '#0178d4',
  },
});

const Rating: React.FC<{ i18n: I18nModel; onClick: any, maxRating?: number }> = ({ i18n, onClick, maxRating = 5 }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);

  const classes = useRatingStyles();

  const onStarClick = useCallback((index: number) => {
    setRating(index);
    onClick?.(index);
  }, [onClick]);

  const iconInactive = <Star16Regular />;
  const iconActive = <Star16Filled className={classes.starFilled} />;

  return (
    <div className="star-rating">
      {[...Array(maxRating)].map((_, index) => {
        const idx = index + 1;
        const icon = idx <= (hover || rating) ? iconActive : iconInactive;

        return (
          <Button
            className={mergeClasses('star-rating-button', classes.button)}
            appearance="transparent"
            icon={icon}
            aria-label={i18n.getString(_TL_('{{currentRating}} of {{maxRating}} stars'), { currentRating: idx.toString(), maxRating: maxRating.toString() })}
            key={idx}
            onClick={() => onStarClick(idx)}
            onMouseEnter={() => setHover(idx)}
            onMouseLeave={() => setHover(rating)}
          />
        );
      })}
    </div>
  );
};

interface LiveAgentPostChatSurveyProps {
  logger: ILogger;
  styles: any;
  activity: any;
}

export const LiveAgentPostChatSurvey: React.FC<LiveAgentPostChatSurveyProps> = ({
  logger, styles, activity,
}) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const startDate = new Date().toISOString();
  const [isSurveySubmitted, setIsSurveySubmitted] = React.useState(false);
  const [isSatisfied, setIsSatisfied] = useState('');
  const [rating, setRating] = useState(0);
  const additionalInfo = useRef('');

  const onSurveySubmit = useCallback(() => {
    const surveyResponse = {
      conversationId: activity.conversation.id,
      firstName: activity.firstName,
      lastName: activity.lastName,
      localeId: activity.localeId,
      localeName: activity.localeName,
      isSatisfied,
      rating,
      additionalInfo: additionalInfo.current,
      startDate,
      submitDate: new Date().toISOString(),
    };
    logger?.trace(`Copilot - Live Agent: Post chat survey response - ${JSON.stringify(surveyResponse)}`);
    sendSurveyResponse(logger, surveyResponse);
    setIsSurveySubmitted(true);
    dispatchStructuredLogUserEngagementEvent({
      activity: {} as any,
      options: {
        liveAgentPostChatSurveyResponse: {
          isResolved: isSatisfied,
          rating,
          feedback: additionalInfo.current,
        },
      },
    });
  }, [activity, isSatisfied, logger, rating, startDate]);

  const onRadioSelect = useCallback((event: React.SyntheticEvent) => {
    const { value } = event.target as HTMLInputElement;
    if (value) {
      setIsSatisfied(value);
    }
  }, []);

  const onRatingSelect = useCallback((value: number) => {
    setRating(value);
  }, []);

  const onTextChange = useCallback((event: any) => {
    additionalInfo.current = event.target.value;
  }, []);

  const isSurveySubmitEnabled = useMemo(() => (!!isSatisfied && rating > 0), [isSatisfied, rating]);

  return (
    !isSurveySubmitted
      ? (
        <BaseMessage className={mergeClasses('live-agent-card-survey', styles.liveAgentSurveyCard)}>
          <div className={mergeClasses('live-agent-card-survey-content', styles.liveAgentCardContent)}>
            <Label>{i18n.getString(_TL_('We appreciate your feedback'))}</Label>
            <div style={{ marginBottom: 8 }}>
              <Label style={{ fontWeight: 100 }} size="small" required>
                {i18n.getString(_TL_('Did we resolve your issue?'))}
              </Label>
              <RadioGroup className={mergeClasses('live-agent-card-survey-radio', styles.liveAgentCardRadio)} onClick={onRadioSelect}>
                <Radio value="Yes, in one contact." label={i18n.getString(_TL_('Yes, in one contact.'))} />
                <Radio value="Yes, in multiple contacts." label={i18n.getString(_TL_('Yes, in multiple contacts.'))} />
                <Radio value="No." label={i18n.getString(_TL_('No.'))} />
              </RadioGroup>
            </div>

            <div>
              <Label style={{ fontWeight: 100 }} size="small" required>
                {i18n.getString(_TL_('How satisfied were you with this Microsoft Advertising support experience?'))}
              </Label>
              <div style={{ marginTop: 4 }}>
                <Rating i18n={i18n} onClick={onRatingSelect} />
              </div>
            </div>
            <div>
              <Label style={{ fontWeight: 100, marginBottom: 4 }} size="small">
                {i18n.getString(_TL_('Tell us more about your experience'))}
                <Textarea className="live-agent-card-survey-textarea" size="small" resize="vertical" onChange={onTextChange} />
              </Label>
            </div>

            <Button className="live-agent-card-survey-submit" style={{ marginTop: 8 }} onClick={onSurveySubmit} appearance="primary" size="small" disabled={!isSurveySubmitEnabled}>
              {i18n.getString(_TL_('Submit'))}
            </Button>
          </div>
        </BaseMessage>
      )
      : (
        <BaseMessage className={mergeClasses('live-agent-card-survey', styles.liveAgentSurveyCard)}>
          <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
            <CheckmarkCircle20Filled color="#13A10E" />
            <Label className="live-agent-card-survey-submitted-message" style={{ fontWeight: 100 }} size="small">{i18n.getString(_TL_('Thank you for your feedback'))}</Label>
          </div>
        </BaseMessage>
      )
  );
};
